import { FC, useContext, useEffect, useReducer } from "react";
import LearnContext from "./LearnContext";
import { answerQuizQuestionFunction, getAuthUserPoolQuizFunction, getPoolQuizQuestionFunction, getPoolQuizzesFunction, getQuizResultsBySubmissionsIdFunction } from "./LearnFunction";
import { AnswerQuizQuestionResponse, AuthPoolQuiz, GetAuthUserPoolQuizResponse, GetPoolQuizQuestionArgs, GetPoolQuizQuestionResponse, GetPoolQuizzesResponse, Quiz, QuizAttempt, QuizResult, QuizStatuses, QuestionResult, LearnStateInterface } from "./learn.interface";
import LearnReducer from "./LearnReducer";
import { ADD_ACTIVE_QUIZZES, ADD_POOL_QUIZZES, CLEAR_PREVIOUS_ATTEMPT, RESET_LEARN_STATE, RESET_QUIZ_STATE, SET_NEXT_QUESTION, SET_QUIZ_INDEX, SET_QUIZ_STATUS, SET_QUIZ_STATUS_AND_START_TIME, START_QUIZ, UPDATE_ACTIVE_QUIZZES, UPDATE_ACTIVE_QUIZ_SUBMISSIONS, UPDATE_QUESTION_DETAIL, UPDATE_QUIZ_INDEX, UPDATE_QUIZ_QUESTION_DETAIL, UPDATE_QUIZ_TRACKER } from "../action";
import AuthContext from "../Auth/AuthContext";
import { useHistory } from "react-router";
import { isQuizExpired } from "../../components/Learn2/LearnQuizTimer";
import { QUIZ_LAST_START_TIME } from "./learn.constant";

const initState: LearnStateInterface = {
    activeQuizzes: null, // Fetched at auth - all current quizzes attempted by user
    quizzes: [], // All quizzes fetched
    quizTracker: null,
    activeQuizSubmissions: [],
    isFetching: false,
    error: null,
    quizIndex: -1,
    questionIndex: -1,
    nextQuestion: null,
    currentQuizQuestionsWithDetails: [],
    quizStatus: undefined,
    quizStartTime: undefined,
}

const LearnState: FC = (props) => {

    const history = useHistory();
    const [state, dispatch] = useReducer(LearnReducer, initState) as [LearnStateInterface, React.Dispatch<any>];

    const { isAuth } = useContext<any>(AuthContext);

    useEffect(() => {
        if (isAuth) {
            getAuthUserPoolQuiz();
        }
    }, [isAuth]);

    /**
     * 
     */

    const getAuthUserPoolQuiz = () => {
        getAuthUserPoolQuizFunction()
        .then((data: GetAuthUserPoolQuizResponse) => {
            dispatch({
                type: ADD_ACTIVE_QUIZZES,
                data: data.getAuthUserPoolQuiz
            })
            // For each active pool, get the submission
            // detail, for now - fetch detail on learn routes only
            let submissionIds = extractAllLastAttemptIds(data.getAuthUserPoolQuiz);
            submissionIds.map((submissionId: string) => {
                getQuizResultsBySubmissionId(submissionId);
            });
        })
        .catch((error: any) => {
            console.error("Error in getAuthUserPoolQuiz")
            // TODO: Handle error
            console.error(error)
        })
    }

    function extractAllLastAttemptIds(authPoolQuizzes: AuthPoolQuiz[]): string[] {
        return authPoolQuizzes.reduce((accumulator: string[], authPoolQuiz) => {
            const ids = authPoolQuiz.quizzes.map(quiz => quiz.lastAttempt.id);
            return accumulator.concat(ids);
        }, []);
    }

    const getPoolQuizzes = (poolId: string) => {
        getPoolQuizzesFunction(poolId)
        .then((data: GetPoolQuizzesResponse) => {
            // Add to all quizzes
            // How to inform component ?
            if (data.getPoolQuizzes.length === 0) return;
            dispatch({
                type: ADD_POOL_QUIZZES,
                data: data.getPoolQuizzes
            })
            
        })
        .catch((error: any) => {
            // TODO: Handle error
            console.error("Error in getPoolQuizzes")
            console.error(error)
        })
    }

    const getPoolQuizQuestion = ( options: GetPoolQuizQuestionArgs) => {
        const {poolId, questionId, quizId, startQuizType} = options
        getPoolQuizQuestionFunction(poolId!, quizId!, questionId!)
        .then((data: GetPoolQuizQuestionResponse) => {


            // TODO: Get quiz start time from tracker
            const lastQuizStartTime = localStorage.getItem(QUIZ_LAST_START_TIME)
            let quizStartTime = Date.now();
            if(startQuizType === 'resume'){
                // get start time from the first submission
                if(lastQuizStartTime){
                    quizStartTime = Number(lastQuizStartTime)
                }
            }

            dispatch({
                type: SET_QUIZ_STATUS_AND_START_TIME,
                data: {
                    quizStatus: QuizStatuses.ON_GOING,
                    quizStartTime
                }
            })

            dispatch({
                type: SET_NEXT_QUESTION,
                data: {
                    quizId,
                    questionId,
                    questionDetail: data.getPoolQuizQuestion,
                }
            })
            // set a new lastQuizStartTime in localstorage
            localStorage.setItem(QUIZ_LAST_START_TIME, quizStartTime.toString())
            if (!existsActiveQuizWithPoolIdAndQuizId(poolId!, quizId!)) {
                dispatch({
                    type: UPDATE_ACTIVE_QUIZZES,
                    data: {
                        poolId,
                        quizzes: [{
                            quizId,
                            numberOfAttempts: 1,
                            lastAttempt: {},
                        }]
                    }
                })
            }
        })
        .catch((error: any) => {
            // TODO: Handle error
            console.log({ error })
        })
    }

    const updateQuizIndex = () => {
        dispatch({
            type: UPDATE_QUIZ_INDEX
        })
    }

    const existsActiveQuizWithPoolIdAndQuizId = (poolId: string, quizId: string) => {
        if (state.activeQuizzes && state.activeQuizzes.length === 0 || state.activeQuizzes === null) return false;
        const pool = state.activeQuizzes.find((apq: AuthPoolQuiz) => {
            return apq.poolId === poolId;
        })
        if (pool) {
            return pool.quizzes.some((quiz: QuizAttempt) => quiz.quizId === quizId) 
        }
        return false;
    }


    const setQuizIndex = (quizIndex: number) => {
        dispatch({
            type: SET_QUIZ_INDEX,
            data: quizIndex
        })
    }

    const setQuizAttempt = (quizTracker: any) => {
        dispatch({
            type: UPDATE_QUIZ_TRACKER,
            data: quizTracker
        })
    }

    const answerQuizQuestion = (poolId: string, quizId: string, questionId: string, answerId: string) => {
        const { quizStartTime, quizzes, quizIndex } = state
        const quiz = quizzes?.[quizIndex]

        // check if time has expired
        const isLiveQuizExpired = isQuizExpired({
            quizStartTime,
            timeLimitInSeconds: quiz?.timeLimitInSeconds
        })

        
        answerQuizQuestionFunction(poolId, quizId, questionId, answerId)
        .then((data: AnswerQuizQuestionResponse) => {
            // Update the next question detail
            // If last question then update quiz index
            // Set the quiz tracker
            dispatch({
                type: UPDATE_QUIZ_TRACKER,
                data: data.answerQuizQuestion.quizTracker
            });
            // Set the active submissions
            dispatch({
                type: UPDATE_ACTIVE_QUIZ_SUBMISSIONS,
                data: [{
                    poolId,
                    quizId,
                    questionId,
                    answerId,
                    isLastQuestion: data.answerQuizQuestion.isLastQuestion,
                    answerResponse: {
                        id: data.answerQuizQuestion.id,
                        points: data.answerQuizQuestion.points,
                        isCorrect: data.answerQuizQuestion.isCorrect,
                    }
                }]
            });

            getAuthUserPoolQuiz();

            if(isLiveQuizExpired) {
                // redirect to result page
                dispatch({
                    type: SET_QUIZ_STATUS,
                    data: {
                        quizStatus: QuizStatuses.EXPIRED
                    }
                });
                setTimeout(()=> {
                    history.push(`/learn-result/${poolId}`);
                }, 2000)
                
                return;
        
            }  else {
                if (data.answerQuizQuestion.isLastQuestion) {
                    // TODO: Show the result here
                    history.push(`/learn-result/${poolId}`);
                } else {
                    dispatch({
                        type: SET_NEXT_QUESTION,
                        data: {
                            quizId,
                            questionId: data.answerQuizQuestion.nextQuestion.id,
                            questionDetail: data.answerQuizQuestion.nextQuestion
                        }
                    })
                }
            }
        })
        .catch((error: any) => {
            // TODO: Handle error
        })
    }

    const getQuizResultsBySubmissionId = (submissionId: string) => {
        getQuizResultsBySubmissionsIdFunction(submissionId)
        .then((data: QuizResult) => {
            const submissions = data.questionResults.map((result: QuestionResult) => {
                return {
                    poolId: data.poolId,
                    quizId: data.quizId,
                    questionId: result.question.id,
                    answerId: result.userAnswer.id,
                    isLastQuestion: result.userAnswer.isLastQuestion,
                    answerResponse: {
                        id: result.userAnswer.id,
                        points: result.userAnswer.points,
                        isCorrect: result.userAnswer.isCorrect
                    }                
                }
            })
            dispatch({
                type: UPDATE_ACTIVE_QUIZ_SUBMISSIONS,
                data: submissions
            });

             // Additionally we need to update current quiz questions with details
             const quizQuestionWithDetails = data.questionResults.map((result: QuestionResult) => {
                return {
                    quizId: data.quizId,
                    questionId: result.question.id,
                    questionDetail: result.question
                }
            })
            dispatch({
                type: UPDATE_QUIZ_QUESTION_DETAIL,
                data: quizQuestionWithDetails
            })
        })
        .catch((error: any) => {
            // TODO: Handle error
        })
    }

    const resetLearnState = () => {
        dispatch({
            type: RESET_LEARN_STATE,
        })
        getAuthUserPoolQuiz();
    }

    const resetQuizState = () => {
        dispatch({
            type: RESET_QUIZ_STATE,
        })
    }

    const clearPreviousAttempt = () => {
        dispatch({
            type: CLEAR_PREVIOUS_ATTEMPT
        })
    }

    const hasFailed = (poolId: string) => {
        if (!state.activeQuizzes || state.activeQuizzes.length === 0) return false;
        const poolActiveQuiz = state.activeQuizzes.find((activeQuiz: any) => activeQuiz.poolId === poolId);
        if (poolActiveQuiz) {
            let hasFailed = poolActiveQuiz.quizzes.filter((quiz: any) => {
                return quiz.numberOfAttempts === 3 && quiz.lastAttempt.isSuccessful === false
            });
            if (hasFailed?.length > 0) return true;
        }
        return false;
    }

    return (
        <LearnContext.Provider value={{
            activeQuizzes: state.activeQuizzes,
            quizzes: state.quizzes,
            quizTracker: state.quizTracker,
            nextQuestion: state.nextQuestion,
            quizIndex: state.quizIndex,
            activeQuizSubmissions: state.activeQuizSubmissions,
            currentQuizQuestionsWithDetails: state.currentQuizQuestionsWithDetails,
            quizStartTime: state.quizStartTime,
            quizStatus: state.quizStatus,
            getAuthUserPoolQuiz,
            getPoolQuizzes,
            getPoolQuizQuestion,
            answerQuizQuestion,
            getQuizResultsBySubmissionId,
            updateQuizIndex,
            setQuizIndex,
            resetLearnState,
            resetQuizState,
            clearPreviousAttempt,
            setQuizAttempt,
            hasFailed,
        }}>
            { props.children }
        </LearnContext.Provider>
    )
}

export { LearnState };